import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Minimize2, Maximize2, RefreshCw, Sparkles } from 'lucide-react';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface FormSubmission {
  formType: 'conversion_copy' | 'brand_scaling';
  timestamp: Date;
  data: Record<string, any>;
}

interface CleoProps {
  isVisible?: boolean;
  onFormSubmission?: (submission: FormSubmission) => void;
}

const Cleo: React.FC<CleoProps> = ({ isVisible = true, onFormSubmission }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: "Hi there! I'm Cleo, your AI assistant. I'm here to help with any questions about Manifest Illusions and guide you through our services. What can I help you with today?",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [pendingConsultation, setPendingConsultation] = useState<FormSubmission | null>(null);
  const [lastFormSubmission, setLastFormSubmission] = useState<FormSubmission | null>(null);

  // Listen for form submissions from window events
  useEffect(() => {
    const handleFormSubmission = (event: CustomEvent<FormSubmission>) => {
      const submission = event.detail;
      setLastFormSubmission(submission);
      setPendingConsultation(submission);
      
      // Auto-open chatbot and send confirmation message
      setIsOpen(true);
      setIsMinimized(false);
      
      // Send personalized confirmation message based on form type and data
      setTimeout(() => {
        const confirmationMessage = generateConfirmationMessage(submission);
        addCleoMessage(confirmationMessage);
        
        // Add consultation options after a brief delay
        setTimeout(() => {
          addCleoMessage("Would you like me to arrange a consultation?\n\n• Type 'Yes' to schedule a meeting\n• Type 'No' to receive additional details instead");
        }, 2000);
      }, 1000);
    };

    window.addEventListener('formSubmitted', handleFormSubmission as EventListener);
    
    return () => {
      window.removeEventListener('formSubmitted', handleFormSubmission as EventListener);
    };
  }, []);

  const generateConfirmationMessage = (submission: FormSubmission): string => {
    const { formType, data } = submission;
    
    let serviceType = '';
    let personalizedDetails = '';
    
    if (formType === 'conversion_copy') {
      serviceType = 'conversion copywriting services';
      
      // Extract key details from copywriting form
      const businessName = data.businessName || data.name || 'your business';
      const projectType = data.projectType || 'your project';
      const timeline = data.timeline || '';
      const websiteUrl = data.websiteUrl || '';
      
      personalizedDetails = `I see you're interested in ${projectType.toLowerCase()} for ${businessName}${timeline ? ` with a ${timeline} timeline` : ''}${websiteUrl ? ` for ${websiteUrl}` : ''}. `;
      
    } else if (formType === 'brand_scaling') {
      serviceType = 'brand scaling services';
      
      // Extract key details from brand scaling form
      const companyName = data.companyName || data.name || 'your company';
      const currentRevenue = data.currentRevenue || '';
      const targetGoals = data.targetGoals || '';
      const industry = data.industry || '';
      
      personalizedDetails = `I noticed you submitted details about ${companyName}${industry ? ` in the ${industry} industry` : ''}${currentRevenue ? ` with current revenue of ${currentRevenue}` : ''}${targetGoals ? ` and goals to ${targetGoals}` : ''}. `;
    }
    
    return `${personalizedDetails}Thank you for your submission for our ${serviceType}. We have successfully received your information and will begin reviewing it promptly. Your interest in our services is greatly valued, and we are eager to explore how we can assist you in achieving your objectives.

If you would like to discuss your needs further, I would be pleased to arrange a private consultation at your convenience.

Please let me know if you would like to proceed with scheduling a meeting, or if you would prefer to receive additional details at this time.`;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-suggest after 30 seconds of inactivity
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      if (messages.length === 1) {
        addCleoMessage("I'm still here if you need anything! Feel free to ask about our services, pricing, or anything else you'd like to know.");
      }
    }, 30000);

    return () => clearTimeout(timer);
  }, [isOpen, messages.length]);

  const addMessage = (content: string, isUser: boolean) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      content,
      isUser,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const addCleoMessage = (content: string) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage(content, false);
    }, 800 + Math.random() * 400);
  };

  const getCleoResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();

    // Handle consultation responses
    if (pendingConsultation && (input.includes('yes') || input.includes('schedule') || input.includes('meeting') || input.includes('consultation'))) {
      setPendingConsultation(null);
      return `Perfect! I'll connect you with our team to schedule your consultation. Based on your ${pendingConsultation.formType === 'conversion_copy' ? 'conversion copywriting' : 'brand scaling'} inquiry, they'll be able to dive deep into your specific needs and create a customized strategy for you. You should hear from us within the next few hours to set up a time that works best for you.`;
    }

    if (pendingConsultation && (input.includes('no') || input.includes('not now') || input.includes('later'))) {
      setPendingConsultation(null);
      return `No problem at all! We'll make sure to send you additional details about our ${pendingConsultation.formType === 'conversion_copy' ? 'conversion copywriting services' : 'brand scaling solutions'} via email. Feel free to reach out anytime when you're ready to take the next step. I'm always here to help answer any questions you might have!`;
    }

    // Reference previous form submission in responses
    if (lastFormSubmission && (input.includes('form') || input.includes('submission') || input.includes('submitted'))) {
      const serviceType = lastFormSubmission.formType === 'conversion_copy' ? 'conversion copywriting' : 'brand scaling';
      return `Yes, I have all the details from your ${serviceType} form submission. Your information has been securely stored and our team is already reviewing it. Is there anything specific about your ${serviceType} needs you'd like to discuss while we prepare your customized proposal?`;
    }

    // Greetings
    if (input.includes('hello') || input.includes('hi') || input.includes('hey') || input === 'h') {
      const greetings = [
        "Hello! Great to meet you. How can I assist you today?",
        "Hi there! I'm here to help with anything you need to know about Manifest Illusions.",
        "Hey! Welcome. What brings you here today?",
        "Hello! I'm excited to help you explore what we can create together."
      ];
      return greetings[Math.floor(Math.random() * greetings.length)];
    }

    if (input.includes('how are you') || input.includes('how\'s it going') || input.includes('what\'s up')) {
      return "I'm doing wonderfully, thank you for asking! I'm here and ready to help you with any questions about our services. What's on your mind?";
    }

    if (input.includes('good morning') || input.includes('good afternoon') || input.includes('good evening')) {
      const timeGreeting = input.includes('morning') ? 'morning' : input.includes('afternoon') ? 'afternoon' : 'evening';
      return `Good ${timeGreeting}! Perfect timing to explore what Manifest Illusions can do for you. What would you like to know?`;
    }

    if (input.includes('nice to meet you') || input.includes('pleasure to meet')) {
      return "The pleasure is all mine! I love helping people discover the perfect solutions for their needs. What can I help you explore today?";
    }

    // About Cleo
    if (input.includes('who are you') || input.includes('what\'s your name')) {
      return "I'm Cleo, your AI assistant here at Manifest Illusions. I'm here to help answer questions, guide you through our services, and make sure you get exactly what you're looking for. Think of me as your personal guide!";
    }

    // Gratitude
    if (input.includes('thank you') || input.includes('thanks')) {
      return "You're so welcome! I'm always happy to help. Is there anything else you'd like to know?";
    }

    if (input.includes('you\'re awesome') || input.includes('you\'re great') || input.includes('amazing')) {
      return "That's so kind of you to say! I really enjoy helping people find exactly what they need. What else can I assist you with?";
    }

    // Help and guidance
    if (input.includes('can you help me') || input.includes('need help') || input.includes('help me')) {
      return "Absolutely! That's exactly what I'm here for. Whether you have questions about our services, pricing, process, or anything else - I'm ready to help. What would you like to know?";
    }

    if (input.includes('i don\'t understand') || input.includes('confused') || input.includes('what do you mean')) {
      return "No worries at all! Let me clarify. Manifest Illusions specializes in premium digital design - think stunning websites, powerful brand identities, and compelling visual content. What specific area interests you most?";
    }

    if (input.includes('i\'m not sure') || input.includes('maybe') || input.includes('thinking about it')) {
      return "That's perfectly fine! Taking time to consider is smart. I'm here to help you explore your options without any pressure. What aspects would be most helpful to discuss?";
    }

    // Services
    if (input.includes('service') || input.includes('what do you do') || input.includes('what can you do')) {
      return "Great question! Manifest Illusions creates premium digital experiences - beautiful websites, sophisticated brand identities, compelling copy, and engaging visual content. We focus on luxury aesthetics with powerful functionality. Which area interests you most?";
    }

    if (input.includes('logo') || input.includes('brand') || input.includes('identity')) {
      return "Brand identity is one of our specialties! We create sophisticated visual systems that truly represent who you are - from logos to complete brand guidelines. It's about crafting something that feels authentically you while standing out beautifully. Would you like to know more about our process?";
    }

    if (input.includes('website') || input.includes('web') || input.includes('site')) {
      return "We love creating stunning websites! Our approach focuses on both beauty and performance - sites that captivate visitors while delivering results. From elegant landing pages to full e-commerce platforms, we craft digital experiences that truly represent your vision. What type of website are you considering?";
    }

    if (input.includes('copy') || input.includes('writing') || input.includes('content')) {
      return "Content and copywriting are crucial elements we specialize in! We create compelling copy that speaks to your audience authentically while driving action. Whether it's website copy, marketing materials, or brand messaging, we make sure your voice shines through. What kind of content do you need?";
    }

    // Pricing
    if (input.includes('price') || input.includes('cost') || input.includes('how much')) {
      return "Pricing varies depending on the scope and complexity of your project. We offer everything from individual services to comprehensive packages. I'd be happy to discuss options that fit your budget and goals. Would you like to explore what might work best for your specific needs?";
    }

    if (input.includes('expensive') || input.includes('affordable') || input.includes('budget')) {
      return "I understand budget is an important consideration! We work with various budgets and often have flexible payment options. The investment really depends on what you're looking to achieve. Would it help to discuss what you're hoping to accomplish so I can suggest the most cost-effective approach?";
    }

    // Timeline
    if (input.includes('how long') || input.includes('timeline') || input.includes('delivery') || input.includes('time')) {
      return "Timeline depends on the project scope, but we're pretty efficient! Simple projects might take a week or two, while more comprehensive work could be a few weeks to a month. We always provide clear timelines upfront and keep you updated throughout. What kind of timeline are you working with?";
    }

    // Process
    if (input.includes('process') || input.includes('how it works') || input.includes('steps')) {
      return "Our process is designed to be smooth and collaborative! We typically start with a consultation to understand your vision, then move through strategy, design, and refinement phases. You're involved every step of the way to ensure we're creating exactly what you envision. Would you like me to walk you through it in more detail?";
    }

    // Portfolio/Examples
    if (input.includes('portfolio') || input.includes('examples') || input.includes('work') || input.includes('see')) {
      return "I'd love for you to see our work! You can explore our portfolio right here on the website - just scroll through to see various projects. Each piece showcases our attention to detail and commitment to premium quality. Is there a particular style or type of project you're most interested in seeing?";
    }

    // Contact
    if (input.includes('contact') || input.includes('speak') || input.includes('talk') || input.includes('call')) {
      return "I'd be happy to connect you with our team for a more detailed conversation! You can reach out through the contact form on the website, or I can help guide you to the right next step. What would work best for you?";
    }

    // Casual conversation
    if (input.includes('weather') || input.includes('day')) {
      return "I hope you're having a great day! I'm always here and ready to help with any questions about Manifest Illusions. What can I assist you with?";
    }

    if (input.includes('bye') || input.includes('goodbye') || input.includes('see you')) {
      return "Thank you for stopping by! Remember, I'm always here whenever you have questions. Feel free to reach out anytime - I love helping people find exactly what they need!";
    }

    // Default helpful response
    return "That's interesting! I want to make sure I give you the most helpful information. Could you tell me a bit more about what you're looking for? Whether it's about our services, pricing, process, or anything else - I'm here to help guide you.";
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    addMessage(userMessage, true);
    setInputValue('');

    // Get Cleo's response
    const response = getCleoResponse(userMessage);
    
    // Add Cleo's response with delay
    addCleoMessage(response);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const resetChat = () => {
    setMessages([
      {
        id: '1',
        content: "Hi there! I'm Cleo, your AI assistant. I'm here to help with any questions about Manifest Illusions and guide you through our services. What can I help you with today?",
        isUser: false,
        timestamp: new Date()
      }
    ]);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Chat Bubble Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed bottom-8 right-8 z-50"
          >
            <motion.button
              onClick={() => setIsOpen(true)}
              className="relative w-16 h-16 group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Chat with Cleo"
            >
              <div className="relative w-full h-full">
                <svg
                  viewBox="0 0 64 64"
                  className="w-full h-full drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.3))' }}
                >
                  <path
                    d="M48 12 C54 12 58 16 58 22 L58 34 C58 40 54 44 48 44 L24 44 L16 52 L16 44 C10 44 6 40 6 34 L6 22 C6 16 10 12 16 12 Z"
                    fill="rgba(15, 23, 42, 0.95)"
                    stroke="rgba(148, 163, 184, 0.3)"
                    strokeWidth="1"
                  />
                  
                  <defs>
                    <linearGradient id="luxuryGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="rgba(30, 41, 59, 0.8)" />
                      <stop offset="50%" stopColor="rgba(15, 23, 42, 0.9)" />
                      <stop offset="100%" stopColor="rgba(0, 0, 0, 0.7)" />
                    </linearGradient>
                  </defs>
                  
                  <path
                    d="M48 12 C54 12 58 16 58 22 L58 34 C58 40 54 44 48 44 L24 44 L16 52 L16 44 C10 44 6 40 6 34 L6 22 C6 16 10 12 16 12 Z"
                    fill="url(#luxuryGradient)"
                  />
                  
                  <path
                    d="M48 13 C53 13 57 17 57 22 L57 34 C57 39 53 43 48 43 L24 43 L17 50 L17 43 C11 43 7 39 7 34 L7 22 C7 17 11 13 16 13 Z"
                    fill="none"
                    stroke="rgba(203, 213, 225, 0.1)"
                    strokeWidth="0.5"
                  />
                </svg>
              </div>
              
              <motion.div
                className="absolute inset-0 rounded-full"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0, 0.1, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                style={{
                  background: 'radial-gradient(circle, rgba(148, 163, 184, 0.2) 0%, transparent 70%)',
                }}
              />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed bottom-8 right-8 z-50 ${
              isMinimized ? 'w-80 h-16' : 'w-80 h-[480px]'
            } transition-all duration-300`}
          >
            <div className="relative h-full bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-2xl overflow-hidden">
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-gray-50/40 pointer-events-none" />
              
              {/* Header */}
              <div className="relative bg-white/80 backdrop-blur-sm border-b border-gray-200/30 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <motion.div
                        className="absolute -inset-0.5 bg-indigo-400/20 rounded-full blur-sm"
                        animate={{
                          scale: [1, 1.1, 1],
                          opacity: [0.3, 0.6, 0.3]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="text-gray-800 font-semibold text-base">Cleo</h3>
                      <div className="flex items-center space-x-2">
                        <motion.div
                          className="w-2 h-2 bg-green-500 rounded-full"
                          animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.7, 1, 0.7]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        />
                        <p className="text-green-600 text-sm font-medium">AI Assistant</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <motion.button
                      onClick={resetChat}
                      className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-xl hover:bg-gray-100/50"
                      title="Reset chat"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      onClick={() => setIsMinimized(!isMinimized)}
                      className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-xl hover:bg-gray-100/50"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                    </motion.button>
                    <motion.button
                      onClick={() => setIsOpen(false)}
                      className="text-gray-500 hover:text-red-500 transition-colors p-2 rounded-xl hover:bg-red-50"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <X className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </div>

              {!isMinimized && (
                <>
                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 h-72 scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300">
                    {messages.map((message) => (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[85%] ${message.isUser ? 'order-2' : 'order-1'}`}>
                          <div
                            className={`p-3 rounded-2xl ${
                              message.isUser
                                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg'
                                : 'bg-white text-gray-800 border border-gray-200/50 shadow-sm'
                            }`}
                          >
                            <p className="text-sm leading-relaxed">{message.content}</p>
                          </div>
                          <p className={`text-xs mt-2 ${message.isUser ? 'text-gray-600' : 'text-gray-500'} text-right`}>
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                    
                    {isTyping && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex justify-start"
                      >
                        <div className="bg-white border border-gray-200/50 shadow-sm p-3 rounded-2xl">
                          <div className="flex items-center space-x-2">
                            <Sparkles className="w-4 h-4 text-indigo-500" />
                            <div className="flex space-x-1">
                              {[0, 1, 2].map((i) => (
                                <motion.div
                                  key={i}
                                  className="w-2 h-2 bg-gray-400 rounded-full"
                                  animate={{
                                    scale: [1, 1.4, 1],
                                    opacity: [0.4, 1, 0.4]
                                  }}
                                  transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    delay: i * 0.2
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input Area */}
                  <div className="relative bg-white/80 backdrop-blur-sm border-t border-gray-200/30 p-4">
                    <div className="flex space-x-3">
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask me anything..."
                        className="flex-1 bg-gray-50/80 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 placeholder-gray-500 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-sm transition-all"
                      />
                      <motion.button
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim()}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-gradient-to-br from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white p-3 rounded-xl transition-all duration-200 shadow-lg"
                      >
                        <Send className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Cleo;